import cx from "classnames";

import { Optional } from "common/utils/types";
import { FetchError } from "common/fetch/types";

import Button, { ButtonProps } from "../buttons/Button";
import LoadingContainer from "../loading/LoadingContainer";
import FetchErrorContainer, { FetchErrorContainerProps } from "../errors/FetchErrorContainer";
import ErrorContainer, { ErrorContainerProps } from "../errors/ErrorContainer";
import CloseButton from "../buttons/CloseButton";
import Form from "../forms/Form";
import FetchErrorAlert, { FetchErrorAlertFieldErrors } from "../errors/FetchErrorAlert";

import styles from "../../styles/deprecated/ModalLayout.module.less";

type ModalLayoutProps = {
  children: React.ReactNode;
};

/**
 * @deprecated Replaced by Stackables
 * @see {@link stackables/hooks} for new implementation
 */
const ModalLayout = ({ children }: ModalLayoutProps) => {
  return <div className={styles.layout}>{children}</div>;
};

type ModalHeaderProps = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  onClose: () => void;
};

const ModalHeader = ({ title, description, onClose }: ModalHeaderProps) => {
  return (
    <div className={styles.header}>
      <div className={styles.text}>
        {title && <h5 className={styles.title}>{title}</h5>}
        {description && <div className={styles.description}>{description}</div>}
      </div>
      <CloseButton className={styles.close} onClick={onClose} />
    </div>
  );
};

const ModalContent = ({ children }: ModalLayoutProps) => {
  return <div className={styles.content}>{children}</div>;
};

const ModalActions = ({ children }: ModalLayoutProps) => {
  return <div className={styles.actions}>{children}</div>;
};

const ModalActionsButton = (props: ButtonProps & { className?: never }) => {
  return <Button {...props} className={styles.button} />;
};

const ModalAlerts = ({ children }: ModalLayoutProps) => {
  return <div className={styles.alerts}>{children}</div>;
};

type ModalFormProps = {
  canSubmit: boolean;
  onSubmit: () => void;
  error?: Optional<FetchError>;
  fieldErrors?: FetchErrorAlertFieldErrors;
  isLoading?: boolean;
  submitText: string;
  children: React.ReactNode;
  hideSubmit?: boolean;
  size?: "m" | "l";
};

const ModalForm = ({
  canSubmit,
  onSubmit,
  children,
  error,
  fieldErrors,
  isLoading,
  submitText,
  hideSubmit = false,
  size = "m",
}: ModalFormProps) => {
  return (
    <Form className={cx(styles.form, styles[size])} onSubmit={onSubmit} canSubmit={canSubmit}>
      <ModalContent>
        {error && <FetchErrorAlert error={error} fieldErrors={fieldErrors} />}
        <div className={styles.body}>{children}</div>
      </ModalContent>
      <ModalActions>
        {!hideSubmit && (
          <ModalActionsButton disabled={!canSubmit} type="submit" isLoading={isLoading}>
            {submitText}
          </ModalActionsButton>
        )}
      </ModalActions>
    </Form>
  );
};

type ModalFormGroupProps = {
  children: React.ReactNode;
  align?: "vertical" | "horizontal";
};

const ModalFormGroup = ({ children, align = "vertical" }: ModalFormGroupProps) => {
  return (
    <div className={`${styles.group} ${align === "horizontal" ? styles.row : ""}`}>{children}</div>
  );
};

const ModalFormLabel = ({ children }: ModalLayoutProps) => {
  return <div className={styles.label}>{children}</div>;
};

const ModalFetchError = (props: FetchErrorContainerProps) => {
  return (
    <div className={styles.error}>
      <FetchErrorContainer {...props} />
    </div>
  );
};

const ModalLoading = () => {
  return (
    <div className={styles.loading}>
      <LoadingContainer />
    </div>
  );
};

const ModalError = (props: ErrorContainerProps) => {
  return (
    <div className={styles.error}>
      <ErrorContainer {...props} />
    </div>
  );
};

export default Object.assign(ModalLayout, {
  Header: ModalHeader,
  Content: ModalContent,
  Form: Object.assign(ModalForm, { Group: ModalFormGroup, Label: ModalFormLabel }),
  Alerts: Object.assign(ModalAlerts),
  Actions: Object.assign(ModalActions, { Button: ModalActionsButton }),
  FetchError: ModalFetchError,
  Loading: ModalLoading,
  Error: ModalError,
});
